import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from '../../lib/redux/hooks';
import { colors } from "../../ui/colors";
import { ArrowLeftIcon } from "../../ui/icons/arrow-left";
import { Layout } from "../../ui/layout";
import { CustomAccordion } from '../../ui/molecules/accordion';
import { ButtonComponent } from "../../ui/molecules/button/newButton";
import { Calendar } from "../../ui/molecules/calendar";
import { MadfarmInput } from '../../ui/molecules/input/newInput';
import { MadfarmStaticSelect } from '../../ui/molecules/select/newSelect/static';
import { ErrorPopup } from '../../ui/organisms/errorPopup';
import { MenuItems } from "../../ui/organisms/navbar/dto";
import { SuccessPopup } from '../../ui/organisms/successPopup';
import { Province, Region, regioniProvinceMap, regions } from "../../utils";
import { provinceComuniMap } from "../../utils/listaComuni";
import { BusinessTechnicianValidation, CreateTechnicianCreation, GetBusinessTechnicianById, resetBusinessTechnicianValidationStatus, resetCreateStatus, resetCreatedTechnicianId, setActivationDate, setAddress, setCity, setCompanyName, setCountryRegion, setDeactivationDate, setEmail, setFiscalCode, setMobilePhone, setPec, setPhone, setProvince, setSelectedTech, setVat, setZipCode } from "./slice";

type OptionType = {
    value: string;
    label: string;
};

export function AddNewTech() {
    const navigate = useNavigate()
    const businessTechinician = useAppSelector(state => state.businessTechinician)
    const dispatch = useAppDispatch()

    const handleToggleAccordion = (panel: string) => {
        setExpanded(expanded === panel ? false : panel);
    };

    const [expanded, setExpanded] = React.useState<string | false>("panel1");
    const [provinceOptions, setProvinceOptions] = useState<OptionType[]>([]);
    const [selectedRegion, setSelectedRegion] = useState<OptionType | null>(null);
    const [comuneOptions, setComuneOptions] = useState<OptionType[]>([]);
    const [selectedComune, setSelectedComune] = useState<OptionType | null>(null);
    const [selectedProvince, setSelectedProvince] = useState<OptionType | null>(null);

    //rp
    const regionOptions: OptionType[] = Array.from(regioniProvinceMap.keys()).map(region => ({
        value: region,
        label: region
    }));

    useEffect(() => {
        if (selectedRegion) {
            const foundRegion = regions.find((region: Region) => region.name === selectedRegion.toString());
            if (foundRegion) {
                const newProvinceOptions = foundRegion.provinces.map((province: Province) => ({
                    value: province.code,
                    label: province.name
                }));
                setProvinceOptions(newProvinceOptions);
            } else {
                setProvinceOptions([]);
            }
        } else {
            setProvinceOptions([]);
        }
    }, [selectedRegion]);

    useEffect(() => {
        if (selectedProvince) {
            const comuni = provinceComuniMap.get(selectedProvince.toString()) || [];
            const newComuneOptions = comuni.map((comune: string) => ({
                value: comune,
                label: comune
            }));
            setComuneOptions(newComuneOptions);
        } else {
            setComuneOptions([]);
        }
    }, [selectedProvince]);

    const handleRegionChange = (selectedOption: OptionType | null) => {
        setSelectedRegion(selectedOption);
        setSelectedProvince(null); // Reset province when region changes
        setSelectedComune(null);
    };

    const handleProvinceChange = (selectedOption: OptionType | null) => {
        setSelectedProvince(selectedOption);
        setSelectedComune(null);
    };

    const hasAnagraficaErrors =
        businessTechinician.technicianErrors.companyName ||
        businessTechinician.technicianErrors.fiscalCode ||
        businessTechinician.technicianErrors.vat;

    const hasSedeLegaleErrors =
        businessTechinician.technicianErrors.address ||
        businessTechinician.technicianErrors.countryRegion ||
        businessTechinician.technicianErrors.province ||
        businessTechinician.technicianErrors.city ||
        businessTechinician.technicianErrors.zipCode;

    const hasContattiErrors =
        businessTechinician.technicianErrors.phone ||
        businessTechinician.technicianErrors.mobilephone;

    const hasDateErrors = businessTechinician.technicianErrors.activationDate;

    useEffect(() => {
        if (businessTechinician.businessTechnicianValidationStatus !== 'failed') {
            return;
        }

        if (hasAnagraficaErrors) {
            setExpanded('panel1');
            return;
        }
        if (hasSedeLegaleErrors) {
            setExpanded('panel2');
            return;
        }
        if (hasContattiErrors) {
            setExpanded('panel4');
            return;
        }
        if (hasDateErrors) {
            setExpanded('panel5');
        }
    }, [
        businessTechinician.businessTechnicianValidationStatus,
        hasAnagraficaErrors,
        hasSedeLegaleErrors,
        hasContattiErrors,
        hasDateErrors
    ]);

    return (
        <Layout
            menuItem={MenuItems.TECNICO_AZIENDALE}
            breadcrumbItems={['Tecnico Aziendale', 'Nuovo Tecnico Aziendale']}
            headerLabel="Tecnico Aziendale"
            headerIcon={
                <ArrowLeftIcon onClick={() => navigate('/tecnico-aziendale')} color={colors.brandPrimary[50]} size={0} />
            }
            headerChildren={
                <div className="flex items-center h-full w-full justify-end gap-2">
                    <ButtonComponent
                        size={"sm"}
                        iconPosition={"off"}
                        variant={"solid"}
                        label="Crea Tecnico"
                        color={"orange"}
                        onClick={() => {
                            dispatch(BusinessTechnicianValidation(businessTechinician.createBusinessTechnician))
                                .unwrap()
                                .then(() => {
                                    dispatch(resetBusinessTechnicianValidationStatus())
                                    dispatch(CreateTechnicianCreation(businessTechinician.createBusinessTechnician))
                                })
                                .catch(() => {
                                    // Validation errors are shown on fields and highlighted accordions.
                                })
                        }}
                    />
                </div>
            }
        >
            {
                (businessTechinician.technicianCreationStatus === 'successfully' || businessTechinician.technicianCreationStatus === 'failed') ?
                    <div className="px-6 pt-3 pb-1 flex flex-col gap-2">
                        <SuccessPopup
                            active={businessTechinician.technicianCreationStatus === 'successfully'}
                            message="Dati salvati correttamente."
                            onClose={() => {
                                const createdId = businessTechinician.createdTechnicianId
                                dispatch(resetCreateStatus('idle'))
                                if (createdId) {
                                    dispatch(setSelectedTech(createdId))
                                    dispatch(GetBusinessTechnicianById(createdId))
                                    dispatch(resetCreatedTechnicianId())
                                    navigate('/editTecnicoAziendale/' + createdId)
                                }
                            }}
                        />
                        <ErrorPopup
                            active={businessTechinician.technicianCreationStatus === 'failed'}
                            message="Si è verificato un errore durante la creazione dell'utente"
                            onClose={() => {
                                dispatch(resetCreateStatus('idle'))
                            }}
                        />
                    </div>
                    : null
            }
            <div className="p-2 flex flex-col gap-6">
                <CustomAccordion
                    id="panel1"
                    title={<span className="text-uppercase text-white label-input">Anagrafica</span>}
                    expanded={expanded === 'panel1'}
                    onChange={() => handleToggleAccordion('panel1')}
                    detailsClassName="flex flex-col gap-4"
                >
                    <div className='grid grid-cols gap-4'>
                        <MadfarmInput
                            required
                            error={businessTechinician.technicianErrors.companyName}
                            supportingText={businessTechinician.technicianErrors.companyName ? 'Ragione sociale mancante' : undefined}
                            label={'Ragione Sociale'}
                            placeholder="Ragione Sociale"
                            onChangeText={(text) => dispatch(setCompanyName(text))}
                        />
                    </div>
                    <div className='grid grid-cols-2 gap-4'>
                        <MadfarmInput
                            error={businessTechinician.technicianErrors.fiscalCode}
                            supportingText={businessTechinician.technicianErrors.fiscalCode ? 'Deve essere compreso tra 11 e 16 caratteri!' : undefined}
                            label={'Codice Fiscale'}
                            placeholder="Codice Fiscale"
                            onChangeText={(text) => dispatch(setFiscalCode(text))}
                        />
                        <MadfarmInput
                            error={businessTechinician.technicianErrors.vat}
                            supportingText={businessTechinician.technicianErrors.vat ? 'Deve essere di 11 caratteri!' : undefined}
                            label={'Partita Iva'}
                            placeholder="Partita Iva"
                            onChangeText={(text) => dispatch(setVat(text))}
                        />
                    </div>
                </CustomAccordion>
                <CustomAccordion
                    id="panel2"
                    title={<span className="text-uppercase text-white label-input">Sede Legale</span>}
                    expanded={expanded === 'panel2'}
                    onChange={() => handleToggleAccordion('panel2')}
                    detailsClassName="flex flex-col gap-4"
                >
                    <div className='grid grid-cols gap-4'>
                        <MadfarmInput
                            required
                            label={'Indirizzo'}
                            error={businessTechinician.technicianErrors.address}
                            supportingText={businessTechinician.technicianErrors.address ? 'Indirizzo mancante' : undefined}
                            placeholder="Indirizzo"
                            onChangeText={(text) => dispatch(setAddress(text))}
                        />
                    </div>
                    <div className='grid grid-cols-2 gap-4'>
                        <MadfarmStaticSelect
                            required
                            label="Regione"
                            noOptionsMessage="Nessuna regione trovata"
                            error={businessTechinician.technicianErrors.countryRegion}
                            errorLabel={'Regione mancante'}
                            value={businessTechinician.createBusinessTechnician.countryRegion}
                            defaultValue={businessTechinician.createBusinessTechnician.countryRegion}
                            placeholder={'Regione'}
                            options={regionOptions}
                            onChange={(value) => {
                                //@ts-ignore
                                const newValue = value !== null ? value.value : null;
                                handleRegionChange(newValue as unknown as OptionType | null);
                                dispatch(setCountryRegion(newValue))
                            }}
                        />
                        <MadfarmStaticSelect
                            required
                            label="Provincia"
                            noOptionsMessage="Nessuna provincia trovata"
                            placeholder={'Provincia'}
                            value={businessTechinician.createBusinessTechnician.province}
                            defaultValue={businessTechinician.createBusinessTechnician.province}
                            error={businessTechinician.technicianErrors.province}
                            errorLabel={'Provincia mancante'}
                            options={provinceOptions}
                            onChange={(value) => {
                                //@ts-ignore
                                const newValue = value !== null ? value.value : null;
                                handleProvinceChange(newValue as unknown as OptionType | null);
                                dispatch(setProvince(newValue))
                            }}

                        />
                    </div>
                    <div className='grid grid-cols-2 gap-4'>
                        <MadfarmStaticSelect
                            required
                            label="Comune"
                            noOptionsMessage="Nessun comune trovata"
                            error={businessTechinician.technicianErrors.city}
                            errorLabel={'Città mancante'}
                            value={businessTechinician.createBusinessTechnician.city}
                            defaultValue={businessTechinician.createBusinessTechnician.city}
                            placeholder={'Comune'}
                            options={comuneOptions}
                            onChange={(value) => {
                                //@ts-ignore
                                const newValue = value !== null ? value.value : null;
                                dispatch(setCity(newValue))
                            }}
                        />
                        <MadfarmInput
                            required
                            error={businessTechinician.technicianErrors.zipCode}
                            supportingText={businessTechinician.technicianErrors.zipCode ? 'Deve essere di 5 cifre!' : undefined}
                            label={'CAP'}
                            placeholder="CAP"
                            onChangeText={(text) => dispatch(setZipCode(text))}
                        />
                    </div>
                </CustomAccordion>
                <CustomAccordion
                    id="panel4"
                    title={<span className="text-uppercase text-white label-input">Contatti</span>}
                    expanded={expanded === 'panel4'}
                    onChange={() => handleToggleAccordion('panel4')}
                    detailsClassName="flex flex-col gap-4"
                >
                    <div className='grid grid-cols-2 gap-4'>
                        <MadfarmInput
                            required
                            error={businessTechinician.technicianErrors.phone}
                            supportingText={businessTechinician.technicianErrors.phone ? 'Deve essere compreso tra 7 e 15 caratteri!' : undefined}
                            label={'Telefono'}
                            placeholder="Telefono"
                            onChangeText={(text) => dispatch(setPhone(text))}
                        />
                        <MadfarmInput
                            required
                            error={businessTechinician.technicianErrors.mobilephone}
                            supportingText={businessTechinician.technicianErrors.mobilephone ? 'Deve essere compreso tra 9 e 13 caratteri!' : undefined}
                            label={'Cellulare'}
                            placeholder="Cellulare"
                            onChangeText={(text) => dispatch(setMobilePhone(text))}
                        />
                    </div>
                    <div className='grid grid-cols-2 gap-4'>
                        <MadfarmInput
                            label={'Email'}
                            placeholder="Email"
                            onChangeText={(text) => dispatch(setEmail(text))}
                        />
                        <MadfarmInput
                            label={'PEC'}
                            placeholder="PEC"
                            onChangeText={(text) => dispatch(setPec(text))}
                        />
                    </div>
                </CustomAccordion>
                <CustomAccordion
                    id="panel5"
                    title={<span className="text-uppercase text-white label-input"> Data ingresso / Uscita</span>}
                    expanded={expanded === 'panel5'}
                    onChange={() => handleToggleAccordion('panel5')}
                    detailsClassName="flex flex-col gap-4"
                >
                    <div className='grid grid-cols-2 gap-4'>
                        <Calendar
                            required
                            label='Data Ingresso'
                            selected={businessTechinician.createBusinessTechnician.activationDate}
                            errorLabel={businessTechinician.technicianErrors.activationDate ? "Data mancante" : ""}
                            onChange={(date) => dispatch(setActivationDate(date))}
                        />
                        <Calendar
                            label='Data Uscita'
                            selected={businessTechinician.createBusinessTechnician.deactivationDate}
                            onChange={(date) => dispatch(setDeactivationDate(date))}
                        />
                    </div>
                </CustomAccordion>
            </div>
        </Layout>
    )
}